import { constants, continuousScanBridge, socket, uiRuntimeConfig } from 'lib_ui-services';
import logging from '@sstdev/lib_logging';
import lodash from 'lodash';
import getAllFeatureFlags from '../../../utilities/getAllFeatureFlags';
const { throttle } = lodash;

export const _private = {
    RESYNC_THROTTLE_MILS: 60000,
    uiRuntimeConfig,
    socket,
    continuousScanBridge,
    resync: () => {
        /*placeholder for code clarity (see below for helper method)*/
    },

    delayRandomly: (callback, upToSeconds = 10) => {
        const delayMs = Math.floor(Math.random() * upToSeconds * 1000);
        return new Promise(resolve => {
            setTimeout(() => {
                callback();
                resolve();
            }, delayMs);
        });
    }
};
export default {
    verb: 'doingUpdate',
    namespace: 'application',
    relation: 'network',
    description: 'Trigger Reconnect actions upon network change',
    //this is the actual logic:
    logic: doingUpdate
};

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").Context} Context
 */

/**
 * @param {{
 *   data: T;
 *   prerequisiteResults: object[];
 *   context: Context;
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 * @returns {T}
 */
async function doingUpdate({ data, dispatch, log, context }) {
    if (data == null) return;
    const { isOnline, isConnected, isServerReachable, type, effectiveType } = data;

    // Tells native Continuous Scan about every isOnline transition regardless of which screen is
    // open - this rule fires exactly once app-wide (registered once per session by the
    // orchestrator), unlike MarkRecord's own per-record effect, which stops existing the moment a
    // user navigates away from the record (e.g. right after Submit). Without this, a Submit that
    // queued its SessionFinish/reads while offline (see ContinuousScanModule.
    // enqueuePendingLifecycleMessage) would only sync again once the user starts a next scan,
    // rather than as soon as they're simply back online. setNetworkStatus itself is a no-op when
    // no native module is registered (mock mode) - see continuousScanBridge.js.
    // isOnline !== undefined: this rule fires for any network-state change (type, effectiveType,
    // isServerReachable, ...) not only isOnline transitions. Guard so that metadata-only updates
    // (WiFi→LTE while isOnline stays true) don't cause a redundant bridge call; the native side's
    // own getAndSet already de-dupes same-value calls, but skipping them here is cleaner.
    if (__SST_REACT_NATIVE__ && isOnline !== undefined) {
        _private.continuousScanBridge.setNetworkStatus({ isOnline }).catch(err => {
            logging.warn('[ContinuousScan] setNetworkStatus failed:', err);
        });
    }

    if (isConnected) {
        log.debug(
            `Network connected over ${type} with speed ${effectiveType}. Server is ${
                !isServerReachable ? 'not ' : ''
            } reachable.`
        );
        if (isOnline) {
            if (!getAllFeatureFlags(context).includes('PREVENT_SYNC_ON_NETWORK_CHANGE')) {
                // Spread resync attempts across the configured window so clients
                // do not all reconnect at once after network recovery.

                const delaySeconds = _private.uiRuntimeConfig.getResyncRandomDelaySeconds();
                await _private.delayRandomly(() => _private.resync(dispatch, log), delaySeconds);
            }
        } else {
            //this will display on the web whenever the socket connection drops while the wifi remains
            //which includes when you log out. can't have that.
            //on native it will work fine, only when the internet becomes unreachable, while the wifi is connected

            if (__SST_REACT_NATIVE__) {
                const message = `Network connected${
                    type === 'unknown' ? '' : ' over ' + type
                } with speed equivalent to ${effectiveType}, but server is not reachable.`;
                dispatch(
                    {
                        isError: true, //todo: gl2025 remove
                        variant: 'error',
                        type: constants.notificationTypes.TOAST,
                        message,
                        timeout: _private.uiRuntimeConfig.getNotificationTimeout()
                    },
                    { verb: 'pop', namespace: 'application', relation: 'notification' }
                );
                logging.debug('[RE] ' + message);
            }
        }
    } else {
        const message = 'Network connection lost';
        log.debug(message);
        //this will display any time wifi is lost
        dispatch(
            {
                isError: true, //todo: gl2025 remove
                variant: 'error',
                type: constants.notificationTypes.TOAST,
                message,
                timeout: _private.uiRuntimeConfig.getNotificationTimeout()
            },
            { verb: 'pop', namespace: 'application', relation: 'notification' }
        );
    }
}

// If network flickers on and off, don't resync every time.
_private.resync = throttle(
    async (dispatch, log) => {
        //reconnect sockets
        _private.socket.reconnect();

        //resync
        log.debug('[RE] Initiating full sync');

        // This is super annoying when you are developing, so we only do it in production

        if (__PRODUCTION__ || __UNIT_TESTING__) {
            await dispatch(
                {},
                { verb: 'update', namespace: 'application', relation: 'useCase', type: 'syncAllDataToLocal' },
                false
            );
        }
    },
    _private.RESYNC_THROTTLE_MILS,
    { leading: true, trailing: false }
);
